import React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import styles from "./Dropdown.module.css";
import clsx from "clsx";

// 위치 alignment 타입 정의
export type DropdownMenuAlignment = "start" | "center" | "end";

// 위치 side 타입 정의
export type DropdownMenuSide = "top" | "right" | "bottom" | "left";

// DropdownMenu 속성 정의
export interface DropdownMenuProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> {
  children: React.ReactNode;
  className?: string;
}

// DropdownMenuTrigger 속성 정의
export interface DropdownMenuTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> {
  children: React.ReactNode;
  className?: string;
}

// DropdownMenuContent 속성 정의
export interface DropdownMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {
  children: React.ReactNode;
  className?: string;
  sideOffset?: number;
  align?: DropdownMenuAlignment;
  side?: DropdownMenuSide;
  avoidCollisions?: boolean;
  collisionPadding?: number | { top?: number; right?: number; bottom?: number; left?: number };
  sticky?: "partial" | "always";
  hideWhenDetached?: boolean;
  width?: string | number; // width 속성 추가
}
// DropdownMenuItem 속성 정의
export interface DropdownMenuItemProps {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onSelect?: (event: Event) => void;
}

// 기본 DropdownMenu 컴포넌트
const DropdownMenu = ({ children, ...props }: DropdownMenuProps) => (
  <DropdownMenuPrimitive.Root {...props}>{children}</DropdownMenuPrimitive.Root>
);
DropdownMenu.displayName = "DropdownMenu";

// DropdownMenuTrigger 컴포넌트
const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  DropdownMenuTriggerProps
>(({ children, className, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={clsx(styles.trigger, className)}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Trigger>
));
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

// DropdownMenuContent 컴포넌트
const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(
  (
    {
      children,
      className,
      sideOffset = 5,
      align = "end",
      side = "bottom",
      avoidCollisions = true,
      width,
      collisionPadding = 8,
      ...props
    },
    ref
  ) => (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        side={side}
        avoidCollisions={avoidCollisions}
        collisionPadding={collisionPadding}
        style={{ width: width ? width : "var(--radix-dropdown-menu-trigger-width)" }} // width 적용
        className={clsx(styles.content, className)}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  )
);
DropdownMenuContent.displayName = "DropdownMenuContent";

// 아래 부터는 선택적으로 사용될 컴포넌트
// DropdownMenuItem 속성 정의
export interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  children: React.ReactNode;
  className?: string;
}

// DropdownMenuItem 컴포넌트
const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ children, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Item>
));
DropdownMenuItem.displayName = "DropdownMenuItem";

// DropdownMenuGroup 컴포넌트
const DropdownMenuGroup = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Group>
>(({ ...props }, ref) => (
  <DropdownMenuPrimitive.Group
    ref={ref}
    {...props}
  />
));
DropdownMenuGroup.displayName = "DropdownMenuGroup";

// DropdownMenuRadioGroup 컴포넌트
const DropdownMenuRadioGroup = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioGroup>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioGroup>
>(({ ...props }, ref) => (
  <DropdownMenuPrimitive.RadioGroup
    ref={ref}
    {...props}
  />
));
DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";

// DropdownMenuRadioItem 컴포넌트
const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
};
